// Log colors below are inferred from the user's screenshot and aligned to the
// current Broadsight blue/orange/green families already present in the app.

export const neutralPalette = {
  white: '#FFFFFF',
  black: '#111111',
  textPrimary: '#202124',
  textSecondary: '#5F6368',
  textHelper: '#6B7280',
  borderSubtle: '#E4E9F0',
  borderMuted: '#DADCE0',
  borderStrong: '#C9D3DF',
  surfacePage: '#FBFCFE',
  surfaceCard: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  surfaceRaised: '#F3F5F8',
  shadowSoft: '0 1px 2px rgba(16,24,40,0.10)',
} as const;

export const semanticPalette = {
  success: {
    fill: '#E6F4EA',
    text: '#137333',
  },
  warning: {
    fill: '#FFF4E5',
    text: '#9A3412',
  },
  error: {
    fill: '#FEF2F2',
    text: '#B42318',
  },
  info: {
    fill: '#EEF5FF',
    text: '#174AA2',
  },
} as const;

export const logPalette = {
  service: {
    main: '#2F7FD9',
    dark: '#1F68C1',
    light: '#5A9BE5',
    surface: '#EEF6FF',
    surfaceStrong: '#E4F0FF',
    border: '#BBD3F3',
    borderStrong: '#8DB7EA',
    text: '#355379',
    iconFill: '#2F7FD9',
    iconContrast: '#FFFFFF',
  },
  mediaInteraction: {
    main: '#ED721F',
    dark: '#ED721F',
    light: '#F2AF2A',
    surface: '#FFF4E8',
    surfaceStrong: '#FFE9D3',
    border: '#F7C89D',
    borderStrong: '#F0A35C',
    text: '#6A4D2D',
    iconFill: '#F47C20',
    iconContrast: '#FFFFFF',
  },
  engagement: {
    main: '#4CB86D',
    dark: '#3B9F5D',
    light: '#7BC694',
    surface: '#EDF8EF',
    surfaceStrong: '#E4F5E8',
    border: '#B9E1C5',
    borderStrong: '#8BC8A0',
    text: '#355C45',
    iconFill: '#4CB86D',
    iconContrast: '#FFFFFF',
  },
  content: {
    main: '#1F3F8B',
    dark: '#17316E',
    light: '#3E63B8',
    surface: '#EEF2FF',
    surfaceStrong: '#E3E9FF',
    border: '#B9C7F0',
    borderStrong: '#8299DA',
    text: '#30466F',
    iconFill: '#1F3F8B',
    iconContrast: '#FFFFFF',
  },
} as const;

export const appPalette = {
  primary: {
    main: logPalette.service.main,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F2AF2A',
    contrastText: '#000000',
  },
  tertiary: {
    main: '#5FBA7D',
    contrastText: '#000000',
  },
  mediaInteraction: {
    main: logPalette.mediaInteraction.main,
    contrastText: '#FFFFFF',
  },
  whiteScale: {
    main: '#F5F5F5',
    contrastText: '#000000',
  },
  status: {
    cancelled: '#C0B6F2',
    inProgress: '#E2F6CA',
    completed: '#ABF5D1',
    participation: '#BFD7FF',
    draftedAutotracker: '#F6FD00',
  },
  tone: {
    positive: '#5FBA7D',
    negative: '#F15B50',
    neutral: '#707070',
  },
  complexity: {
    one: '#FBEAB6',
    two: '#F6D15B',
    three: '#F3BC2F',
    four: '#F19E26',
    five: '#ED721F',
  },
  type: {
    reactive: '#FFBDAD',
    proactive: '#B3F5FF',
    reactiveText: '#8F2D1F',
    proactiveText: '#0F5963',
  },
} as const;

export const analyticsPalette = {
  title: '#111111',
  body: 'rgba(0, 0, 0, 0.72)',
  tooltip: {
    background: '#FFFFFF',
    border: 'rgba(0, 0, 0, 0.10)',
    title: '#1F1F1F',
    body: '#333333',
  },
  card: {
    surface: '#FFFFFF',
    borderSoft: '#E5E7EB',
    shadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
  },
} as const;

export const componentPalette = {
  button: {
    border: neutralPalette.borderMuted,
    shadow: neutralPalette.shadowSoft,
  },
  input: {
    background: neutralPalette.white,
    border: neutralPalette.borderMuted,
    text: neutralPalette.textPrimary,
    placeholder: neutralPalette.textSecondary,
  },
  card: {
    background: neutralPalette.surfaceCard,
    border: neutralPalette.borderSubtle,
  },
  chip: {
    background: neutralPalette.surfaceRaised,
    border: '#D8DEE9',
    text: neutralPalette.textPrimary,
  },
} as const;

const paletteTokens = {
  neutralPalette,
  semanticPalette,
  logPalette,
  appPalette,
  analyticsPalette,
  componentPalette,
} as const;

export default paletteTokens;
